import React, { createContext, useContext, useMemo } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';

import { formFieldLabelText } from './formFieldLabelStyle';
import { colors, fontSize, fontWeight, lineHeight, spacing } from '../../theme';

/**
 * When `showIcons={false}`, labels wrapped with {@link FormFieldLabelWithInfo} omit the (i) affordance
 * for that subtree (used on Opportunity / Site Walk / Site Information / Racks steps).
 */
const FormFieldShowInfoIconsContext = createContext(true);

export function FormFieldShowInfoIconsProvider({
  showIcons,
  children,
}: {
  showIcons: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <FormFieldShowInfoIconsContext.Provider value={showIcons}>
      {children}
    </FormFieldShowInfoIconsContext.Provider>
  );
}

export function useFormFieldShowInfoIcons(): boolean {
  return useContext(FormFieldShowInfoIconsContext);
}

export type FormFieldLabelWithInfoProps = {
  children: React.ReactNode;
  onInfo: () => void;
  /**
   * Short a11y hint for the (i) button, e.g. "Open help for site address" — full panel text is
   * announced via `accessibilityLabel` built from `a11yDescription`.
   */
  infoA11yHint: string;
  /**
   * Full string for screen reader on the button (title + bullets, or a plain-language summary).
   */
  a11yDescription: string;
  required?: boolean;
  /**
   * Side-by-side with a control on the same row: drop bottom gap under the label so spacing comes
   * only from the parent stack (`formFieldGroupGap`), not label `fieldVerticalGap` + stack gap.
   */
  inlineWithControl?: boolean;
  /**
   * Force (i) visibility. When omitted, follows {@link FormFieldShowInfoIconsProvider}
   * (`showIcons` context value).
   */
  showInfoIcon?: boolean;
};

/**
 * Field label with trailing (i); parent opens `FormInfoHelpModal` (same pattern as Project Details).
 */
export const FormFieldLabelWithInfo: React.FC<FormFieldLabelWithInfoProps> = ({
  children,
  onInfo,
  infoA11yHint,
  a11yDescription,
  required = false,
  inlineWithControl = false,
  showInfoIcon: showInfoIconProp,
}) => {
  const iconsFromContext = useFormFieldShowInfoIcons();
  const showInfoIcon =
    showInfoIconProp !== undefined ? showInfoIconProp : iconsFromContext;
  const a11yLabel = useMemo(() => a11yDescription.trim(), [a11yDescription]);
  const legend = (
    <Text
      style={[styles.labelRoot, inlineWithControl && styles.labelRootInline]}
      accessibilityRole="text"
    >
      <Text style={styles.formFieldLabel}>
        {children}
        {required ? <Text style={styles.requiredMark}> *</Text> : null}
      </Text>
      {showInfoIcon ? (
        /* Unicode ⓘ (U+24D8): tappable help in reading flow without nesting SVG in Text. */
        <Text
          onPress={onInfo}
          style={styles.infoInline}
          accessibilityLabel={a11yLabel}
          accessibilityRole="button"
          accessibilityHint={infoA11yHint}
        >
          {' \u00A0\u24D8'}
        </Text>
      ) : null}
    </Text>
  );

  return (
    <View
      style={[
        styles.labelWithInfo,
        inlineWithControl && styles.labelWithInfoInline,
      ]}
      accessibilityElementsHidden={false}
    >
      {/**
       * Put {@link spacing.fieldVerticalGap} on a `View`, not `Text` margin — RN-web often ignores
       * bottom margin on text nodes; matches `FormInput` `labelContainer`.
       */}
      {inlineWithControl ? (
        legend
      ) : (
        <View style={styles.labelBand}>{legend}</View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  /**
   * Single `Text` block: label + required + inline tappable (i). Wraps as one paragraph; (i) follows
   * the last word. Band below legend → control uses {@link spacing.fieldVerticalGap} when not inline.
   */
  labelBand: {
    width: '100%' as const,
    marginBottom: spacing.fieldVerticalGap,
  },
  labelRoot: {
    width: '100%' as const,
    marginBottom: 0,
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formLabel,
    ...Platform.select({
      web: { overflow: 'visible' as const },
      default: {},
    }),
  },
  labelRootInline: {
    marginBottom: 0,
    /** Let the legend shrink-wrap so a sibling control (e.g. Yes/No) sits right after the copy. */
    ...Platform.select({
      web: { width: 'auto' as const },
      default: { alignSelf: 'flex-start' as const },
    }),
  },
  formFieldLabel: {
    ...formFieldLabelText,
  },
  labelWithInfo: {
    width: '100%',
    ...Platform.select({
      web: { overflow: 'visible' as const },
      default: {},
    }),
  },
  /** Pair with a trailing inline control: share one row, leave vertical rhythm to the stack. */
  labelWithInfoInline: {
    flexGrow: 0,
    /** Shrink-wrap legend width; do not shrink below content (RN-web nowrap rows collapse to ~0 otherwise). */
    flexShrink: 0,
    marginRight: spacing.sm,
    ...Platform.select({
      /** Override {@link labelWithInfo} `width: 100%` so the row does not space the control to the far edge. */
      web: {
        width: 'auto' as const,
        maxWidth: '100%' as const,
      },
      default: {
        width: undefined,
        alignSelf: 'flex-start' as const,
      },
    }),
  },
  requiredMark: { color: colors.error },
  /** Tappable (i) glyph: sized to read as secondary control next to the label. */
  infoInline: {
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formLabel,
    fontWeight: fontWeight.semibold,
    color: colors.textSecondary,
  },
});
